import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { Pagination } from '../../../../components/Pagination';
import { SearchField } from '../../../../components/SearchField';

import {
  AddButton,
  Card,
  Container,
  Content,
  FilterPill,
  FilterRow,
  Header,
  PageSubtitle,
  PageTitle,
  TitleAccent,
} from './styles';

import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';
import { useModal } from '../../../../hooks/modal';
import { useToast } from '../../../../hooks/toast';
import { ExercisesService } from '../../../../services/ExercisesService';
import { IExercise, IMuscleGroup } from '../../../../domains/Exercise';
import { getExercisePrimaryMedia } from '../../../../utils/getExercisePrimaryMedia';

const PAGE_SIZE = 10;

const Exercises: React.FC = () => {
  const exercisesService = new ExercisesService();

  const navigate = useNavigate();
  const { modals } = useModal();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [exercises, setExercises] = useState<IExercise[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [muscleGroups, setMuscleGroups] = useState<IMuscleGroup[]>([]);
  const [selectValue, setSelectValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [canCreateCustom, setCanCreateCustom] = useState(true);

  const getMuscleGroups = useCallback(() => {
    exercisesService
      .getMuscleGroups()
      .then(response => {
        setMuscleGroups([{ id: '', name: 'Todos' }, ...response.data]);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos grupos musculares.',
          description:
            'Caso o proplema persista, entre em contato com o administrador do sistema',
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast]);

  const handleRemoveExercise = useCallback(
    async (exercise_id: string) => {
      try {
        await api.delete(`exercises/${exercise_id}`);

        setExercises(current =>
          current.filter(exercise => exercise.id !== exercise_id),
        );
        setTotal(current => current - 1);

        addToast({
          type: 'success',
          title: 'Exercício removido',
        });
      } catch (err: any) {
        if (err?.response?.status === 403) {
          addToast({
            type: 'error',
            title: 'Você não tem permissão para executar essa ação.',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na remoção do exercício',
          description:
            'Houve um erro na remoção do exercicio. Tente novamente mais tarde.',
        });
      }
    },
    [addToast],
  );

  const handleSelectMuscleGroup = useCallback((value: string) => {
    setSelectValue(value);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 400);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectValue]);

  useEffect(() => {
    setLoading(true);
    exercisesService
      .getExercisesOfGym({
        page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        muscle_group_id: selectValue || undefined,
      })
      .then(response => {
        setExercises(response.data.data);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages);
        setCanCreateCustom(response.data.stats.can_create_custom);
      })
      .catch(err => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos exercícios.',
          description: err?.response?.status,
        });
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, selectValue, modals]);

  useEffect(() => {
    getMuscleGroups();
  }, [getMuscleGroups]);

  return (
    <Painel loading={loading}>
      <Container>
        <Header>
          <div>
            <PageTitle>
              Biblioteca de <TitleAccent>exercícios</TitleAccent>
            </PageTitle>
            <PageSubtitle>
              Gerencie os exercícios disponíveis para os seus alunos
            </PageSubtitle>
          </div>
          {canCreateCustom ? (
            <AddButton type="button" onClick={() => navigate('/exercises/new')}>
              <Icon name="add_circle" size={18} color={colors.onPrimary} />
              Novo Exercício
            </AddButton>
          ) : (
            <PageSubtitle>
              Limite de exercícios personalizados desta academia atingido.
            </PageSubtitle>
          )}
        </Header>

        <FilterRow>
          <SearchField
            placeholder="Buscar exercício"
            value={searchText}
            onChange={setSearchText}
          />
          {muscleGroups.map(group => (
            <FilterPill
              key={group.id || 'all'}
              type="button"
              active={selectValue === group.id}
              onClick={() => handleSelectMuscleGroup(group.id as string)}
            >
              {group.name}
            </FilterPill>
          ))}
        </FilterRow>

        <Content>
          {exercises.map(exercise => {
            const primaryMedia = getExercisePrimaryMedia(exercise.media);

            return (
              <Card
                key={exercise.id}
                id={exercise.id}
                thumbnail_url={primaryMedia?.thumbnail_url}
                hasVideo={primaryMedia?.type === 'video'}
                exerciseName={exercise.name}
                muscleGroups={exercise.muscle_groups}
                description={exercise.description}
                isCustom={exercise.is_custom}
                functionChangeButton={() =>
                  navigate(`/exercises/${exercise.id}`, { state: { exercise } })
                }
                functionRemoveButton={
                  exercise.is_custom
                    ? () => handleRemoveExercise(exercise.id)
                    : undefined
                }
              />
            );
          })}
        </Content>

        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={PAGE_SIZE}
          itemLabel="exercícios"
          onPageChange={setPage}
        />
      </Container>
    </Painel>
  );
};

export { Exercises };
